import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { AccountingSubNav } from '../components/AccountingSubNav';
import { BarcodeLabel } from '../components/BarcodeLabel';
import { Search, Printer, Minus, Plus, QrCode, Package, Tags } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { formatINRPaise } from '@/shared/utils/currency';

interface Item {
  _id: string;
  name: string;
  sku: string;
  barcode?: string;
  barcodeType?: string;
  sellingPrice?: number;
  itemType?: string;
}

const NON_PHYSICAL_TYPES = new Set(['service', 'non_stock', 'subscription', 'media_spend']);

export function BarcodePrintPage() {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [qty, setQty] = useState<Record<string, number>>({});

  useEffect(() => {
    setLoading(true);
    api.get('/api/accounting/items?limit=500')
      .then((res) => {
        const list: Item[] = res.data.data?.items || [];
        setItems(list.filter((i) => !NON_PHYSICAL_TYPES.has(i.itemType || '')));
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const q = search.toLowerCase();
  const filtered = items.filter((i) => !q || i.name.toLowerCase().includes(q) || i.sku?.toLowerCase().includes(q));

  const setQtyFor = (id: string, delta: number) => {
    setQty((prev) => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) + delta) }));
  };

  const labels = useMemo(() => {
    const out: { item: Item; copy: number }[] = [];
    for (const item of items) {
      const n = qty[item._id] || 0;
      for (let i = 0; i < n; i++) out.push({ item, copy: i });
    }
    return out;
  }, [items, qty]);

  const totalLabels = labels.length;
  const selectedCount = Object.values(qty).filter((v) => v > 0).length;

  return (
    <div className="">
      <div className="print:hidden">
        <AccountingSubNav />
      </div>

      <div className="max-w-[1580px] mx-auto space-y-6 print:hidden pb-16">
        <PageHeader
          icon={QrCode}
          eyebrow="Finance"
          title="Print Barcodes"
          description={<>Pick items and how many labels each needs, then print — each label uses the item's own SKU as a scannable CODE128 barcode</>}
          actions={
            <><button
                      onClick={() => window.print()}
                      disabled={totalLabels === 0}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                    >
                      <Printer className="w-4 h-4" /> Print {totalLabels > 0 ? `(${totalLabels})` : ''}
                    </button></>
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              label: 'Physical Items in Catalog',
              value: items.length,
              sub: 'Available in Item Master',
              icon: Package,
              bg: 'bg-blue-50 text-blue-600 border-blue-100',
              progress: 'bg-blue-600',
              pct: items.length > 0 ? 100 : 0,
            },
            {
              label: 'Items Selected',
              value: selectedCount,
              sub: 'Configured for printing',
              icon: Tags,
              bg: 'bg-purple-50 text-purple-600 border-purple-100',
              progress: 'bg-purple-600',
              pct: items.length > 0 ? (selectedCount / items.length) * 100 : 0,
            },
            {
              label: 'Total Labels Queued',
              value: totalLabels,
              sub: 'Total label count in print batch',
              icon: QrCode,
              bg: 'bg-emerald-50 text-emerald-600 border-emerald-100',
              progress: 'bg-emerald-500',
              pct: totalLabels > 0 ? Math.min(100, totalLabels * 5) : 0,
            },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.05, ease: [0.4, 0, 0.2, 1] }}
                className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs hover:border-slate-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      {stat.label}
                    </span>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-2xl font-black tracking-tight text-slate-900">
                        {stat.value}
                      </span>
                    </div>
                  </div>
                  <div className={`flex h-11 w-11 items-center justify-center rounded-xl border ${stat.bg} shadow-xs transition-transform group-hover:scale-105`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 font-semibold">
                  <span>{stat.sub}</span>
                  <span className="font-bold text-slate-700">{Math.round(stat.pct)}%</span>
                </div>

                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full ${stat.progress} transition-all duration-500 rounded-full`}
                    style={{ width: `${Math.max(5, stat.pct)}%` }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input className="pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 w-72"
            placeholder="Search item name or SKU…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: [0.4, 0, 0.2, 1] }}
          className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-surface-3"
        >
          {loading ? (
            <div className="py-16 text-center text-sm text-slate-400">Loading items…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <div className="h-14 w-14 rounded-xl bg-slate-100 dark:from-slate-800 dark:to-slate-900 text-slate-400 flex items-center justify-center mx-auto mb-3">
                <QrCode className="w-6 h-6" />
              </div>
              <p className="text-sm text-slate-400">No physical items found in Item Master yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  {['Item', 'SKU / Barcode Value', 'Price', 'Labels to print'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((item) => (
                  <tr key={item._id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{item.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{item.barcode || item.sku || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{item.sellingPrice != null ? formatINRPaise(item.sellingPrice) : '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setQtyFor(item._id, -1)} className="p-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"><Minus className="w-3 h-3" /></button>
                        <span className="w-8 text-center font-mono text-xs">{qty[item._id] || 0}</span>
                        <button onClick={() => setQtyFor(item._id, 1)} className="p-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"><Plus className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </motion.div>
      </div>

      {/* Print sheet — hidden on screen, shown only when printing */}
      <div className="hidden print:grid print:grid-cols-3 print:gap-2 print:p-4">
        {labels.map(({ item, copy }) => (
          <BarcodeLabel
            key={`${item._id}-${copy}`}
            value={item.barcode || item.sku}
            format={(item.barcodeType as any) || 'CODE128'}
            name={item.name}
            price={item.sellingPrice}
          />
        ))}
      </div>

      {/* On-screen preview of the first few labels so the user can see what will print */}
      {totalLabels > 0 && (
        <div className="max-w-[1580px] mx-auto print:hidden pb-16">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Preview</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {labels.slice(0, 8).map(({ item, copy }) => (
              <BarcodeLabel
                key={`preview-${item._id}-${copy}`}
                value={item.barcode || item.sku}
                format={(item.barcodeType as any) || 'CODE128'}
                name={item.name}
                price={item.sellingPrice}
              />
            ))}
          </div>
          {totalLabels > 8 && <p className="text-xs text-slate-400 mt-2">+ {totalLabels - 8} more will print</p>}
        </div>
      )}
    </div>
  );
}

export default BarcodePrintPage;
